// Meta (Facebook) Pixel helper
// Centralises advanced-matching + standard event tracking so Event Match
// Quality can be maximised and browser events can be de-duplicated against
// the Conversions API using a shared eventID.

export const META_PIXEL_ID = '1303476317144991'

type Fbq = (...args: any[]) => void

const getFbq = (): Fbq | null => {
  const fbq = (window as any).fbq
  return typeof fbq === 'function' ? fbq : null
}

// Indonesian phone numbers are usually entered as "08xxxx". Meta matches best
// on E.164-style digits with country code, so normalise to "628xxxx" (no "+",
// Meta hashes it client-side).
export const normalizePhoneID = (raw?: string): string | undefined => {
  if (!raw) return undefined
  let digits = raw.replace(/[^\d]/g, '')
  if (!digits) return undefined
  if (digits.startsWith('0')) digits = '62' + digits.slice(1)
  else if (digits.startsWith('8')) digits = '62' + digits
  // already 62... or other country code -> leave as is
  return digits
}

export interface AdvancedMatching {
  email?: string
  phone?: string
  firstName?: string
  lastName?: string
  externalId?: string
}

// Re-init the pixel with advanced-matching data. fbq hashes these standard
// keys (em/ph/fn/ln) automatically before sending, so we pass raw values.
export const setAdvancedMatching = (data: AdvancedMatching): void => {
  const fbq = getFbq()
  if (!fbq) return

  const userData: Record<string, string> = {}
  if (data.email) userData.em = data.email.trim().toLowerCase()
  const ph = normalizePhoneID(data.phone)
  if (ph) userData.ph = ph
  if (data.firstName) userData.fn = data.firstName.trim().toLowerCase()
  if (data.lastName) userData.ln = data.lastName.trim().toLowerCase()
  if (data.externalId) userData.external_id = data.externalId

  fbq('init', META_PIXEL_ID, userData)
}

// A stable id shared between this browser event and the server-side
// Conversions API event so Meta can de-duplicate them.
export const genEventId = (): string => {
  const c = (window as any).crypto
  if (c && typeof c.randomUUID === 'function') return c.randomUUID()
  return 'e-' + new Date().getTime() + '-' + Math.random().toString(16).slice(2)
}

export const track = (
  event: string,
  custom?: Record<string, unknown>,
  eventID?: string
): void => {
  const fbq = getFbq()
  if (!fbq) return
  if (eventID) fbq('track', event, custom || {}, { eventID })
  else fbq('track', event, custom || {})
}
